/** @jsxRuntime classic */
/** @jsx jsx */
import React from 'react';
import { css } from '@compiled/react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import Button from '@atlaskit/button/default/button';
import StatusWorkflowInReviewIcon from '@atlaskit/icon-lab/core/status-workflow-in-review';
import AvatarGroup from '@atlaskit/avatar-group/avatar-group';
import type { AvatarGroupOverrides, AvatarProps } from '@atlaskit/avatar-group/types';
import Avatar from '@atlaskit/avatar/avatar';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading/heading';

import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper.ts';

import { ApprovalsProgressBar } from '../MultiColorProgressBar/ApprovalsProgressBar.tsx';

export type BannerAvatarProps = AvatarProps & { userId?: string };
export type ProfileCardMode = 'hover' | 'click';
type AvatarGroupItemRender = NonNullable<
	NonNullable<AvatarGroupOverrides['AvatarGroupItem']>['render']
>;
type AvatarGroupItemRenderParams = Parameters<AvatarGroupItemRender>;

const styles = cssMap({
	wrapper: {
		backgroundColor: token('elevation.surface.sunken'),
		padding: token('space.200'),
		borderRadius: token('radius.medium'),
		display: 'flex',
		alignItems: 'center',
		gap: token('space.200'),
	},
	iconWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		padding: token('space.050'),
	},
	leftSection: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		gap: token('space.100'),
		flex: '0 1 auto',
		minWidth: 'min-content',
		overflow: 'hidden',
	},
	leftSectionGrow: {
		flexGrow: 1,
	},
	contentWrapper: {
		flexShrink: 1,
		minWidth: '0px',
		wordBreak: 'break-word',
	},
	progressBarWrapper: {
		flex: '3 1 0%',
		minWidth: '120px',
	},
	actionWrapper: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
		flex: '0 0 auto',
	},
	overflowItem: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		paddingTop: token('space.075'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.150'),
		cursor: 'default',
	},
	overflowItemName: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	clickableAvatar: {
		cursor: 'pointer',
	},
	clickableOverflowItem: {
		cursor: 'pointer',
	},
});

const overflowProfileCardAnchorStyles = css({
	display: 'block',
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Keep the selected profile card anchored while its overflow row scrolls out of view.
	'&:has([aria-expanded="true"])': {
		position: 'sticky',
		top: 0,
		bottom: 0,
		zIndex: 1,
		backgroundColor: token('elevation.surface.overlay'),
	},
});

interface ActionConfig {
	type: 'button';
	label: string;
	onClick: () => void;
}

const ProfileCardAvatar: React.FC<{ avatar: BannerAvatarProps; isClickable: boolean }> = ({
	avatar,
	isClickable,
}) => {
	return (
		<span data-testid="profile-card-avatar" css={[isClickable && styles.clickableAvatar]}>
			<Avatar {...avatar} />
		</span>
	);
};

export interface BannerComponentProps {
	heading: string;
	description?: string;
	avatarData?: BannerAvatarProps[];
	action: ActionConfig;
	showProgressBar?: boolean;
	icon?: React.ReactNode;
	profileCardMode?: ProfileCardMode;
}

const AvatarAndCtaRenderer: React.FC<{
	action: ActionConfig;
	avatarData?: BannerAvatarProps[];
	profileCardMode?: ProfileCardMode;
}> = ({ action, avatarData, profileCardMode }) => {
	const showAvatars = avatarData != null && avatarData.length > 0;

	const renderAvatar = React.useCallback(
		(avatar: BannerAvatarProps) => {
			if (!avatar.userId) {
				return <Avatar {...avatar} />;
			}

			return (
				<ProfileCardWrapper
					userId={avatar.userId}
					fullName={avatar.name}
					trigger={profileCardMode === 'click' ? 'click' : undefined}
				>
					<ProfileCardAvatar avatar={avatar} isClickable={profileCardMode === 'click'} />
				</ProfileCardWrapper>
			);
		},
		[profileCardMode],
	);

	const renderOverflowItem = React.useCallback<AvatarGroupItemRender>(
		(
			_Component: AvatarGroupItemRenderParams[0],
			itemProps: AvatarGroupItemRenderParams[1],
			index: AvatarGroupItemRenderParams[2],
		) => {
			const avatar = itemProps.avatar as BannerAvatarProps;
			const row = (
				<Box xcss={cx(styles.overflowItem, Boolean(avatar.userId) && styles.clickableOverflowItem)}>
					<Avatar {...avatar} size="small" />
					<Text size="small">
						<Box as="span" xcss={styles.overflowItemName}>
							{avatar.name}
						</Box>
					</Text>
				</Box>
			);

			if (!avatar.userId || profileCardMode === undefined) {
				return <React.Fragment key={`overflow-item-${index}`}>{row}</React.Fragment>;
			}

			return (
				<div
					key={`overflow-item-${index}`}
					data-testid="overflow-profile-card-anchor"
					css={overflowProfileCardAnchorStyles}
				>
					<ProfileCardWrapper
						userId={avatar.userId}
						fullName={avatar.name}
						trigger={profileCardMode}
						position="right-start"
						offset={[0, 8]}
						ariaLabel={avatar.name}
						fullWidthTrigger
					>
						{row}
					</ProfileCardWrapper>
				</div>
			);
		},
		[profileCardMode],
	);

	const overrides = React.useMemo<AvatarGroupOverrides>(
		() => ({ AvatarGroupItem: { render: renderOverflowItem } }),
		[renderOverflowItem],
	);

	return (
		<Flex xcss={styles.actionWrapper}>
			{showAvatars && (
				<AvatarGroup
					data={avatarData}
					maxCount={2}
					isTooltipDisabled={profileCardMode !== undefined}
					{...(profileCardMode !== undefined ? { avatar: renderAvatar, overrides } : {})}
				/>
			)}
			<Button appearance="default" onClick={action.onClick}>
				{action.label}
			</Button>
		</Flex>
	);
};

export const BannerComponent: React.FC<BannerComponentProps> = ({
	heading,
	description,
	showProgressBar,
	icon,
	...props
}) => {
	return (
		<Box xcss={styles.wrapper}>
			<Flex xcss={cx(styles.leftSection, !showProgressBar && styles.leftSectionGrow)}>
				<Box xcss={styles.iconWrapper}>
					{icon ?? (
						<StatusWorkflowInReviewIcon label={heading} color={token('color.icon.accent.blue')} />
					)}
				</Box>

				<Stack xcss={styles.contentWrapper} space="space.050">
					<Heading size="small">{heading}</Heading>
					{description && (
						<Text size="medium" color="color.text.subtlest">
							{description}
						</Text>
					)}
				</Stack>
			</Flex>

			{showProgressBar && (
				<Box xcss={styles.progressBarWrapper}>
					<ApprovalsProgressBar />
				</Box>
			)}

			<AvatarAndCtaRenderer {...props} />
		</Box>
	);
};
